import EventTracking, { UserEvent } from '../../libs/parkside/event-tracking';
import { Translator } from '../configurator/translator';
import h from '../../libs/jsx';
import { generateValidId } from '../utils/dom';
import { closeAccordions, initAccordions, openAccordionsBySelectedValues } from '../utils';

const FILTER_LOGIC_OR = 'OR';
const FILTER_LOGIC_AND = 'AND';
const ELEMENT_IDS = {
  resetButton: 'package-filters-reset',
  packageFiltersResetInfo: 'package-filters-reset-info',
  packageFilterSidebar: 'package-filter-sidebar',
  packageFilterTags: 'package-filters-tags',
  packageFilterCounter: 'package-filters-counter',
  packageFilterTagsAssistant: 'package-filters-tags-assistant',
};

const ASSISTANT_COOKIE_NAME = 'filterAssistantDismissed';

export default class PackageFiltering {
  constructor(options = {}) {
    ({
      filterValues: this.filterValueEntities = [],
      translations: this.translations = {},
      packageIds: this.packageIds = [],
      filterAssistant: this.filterAssistantEnabled,
      filterAssistantAutoshow: this.filterAssistantAutoshow,
    } = options);
    this.translator = new Translator({ translations: window.application.translations.terrassenplaner });
    this.filters = {};
    this.filterLabels = {};
    this.filterLogic = {};
    this.filterObjects = {};
    this.resetButton = document.getElementById(ELEMENT_IDS.resetButton);
    this.resetInfo = document.getElementById(ELEMENT_IDS.packageFiltersResetInfo);
    this.filterSidebar = document.getElementById(ELEMENT_IDS.packageFilterSidebar);
    this.tagBar = document.getElementById(ELEMENT_IDS.packageFilterTags);
    this.tagBarAssistant = null;
    this.filterCounterOutput = document.getElementById(ELEMENT_IDS.packageFilterCounter);
    this.activeFilters = {};
    this.activeTags = [];
    this.activePackageIds = [...this.packageIds];
    this._filterEventHandlers = [];
    this._accordionsSidebar = null;
    this._accordionsAssistant = null;

    this.init();
  }

  init() {
    this.processFilterValues();

    if (this.filterSidebar) {
      if (this.filterAssistantEnabled) {
        this.initFilterAssistant();
      }

      this.initNewFilters();

      if (!this.haveSomeFilters()) {
        this.filterSidebar.remove();
      }

      if (this.resetButton) {
        this.initResetButton();
      }

      this.filterPackages();

      if (
        this.haveSomeFilters() &&
        this.filterAssistantEnabled &&
        this.shouldShowFilterAssistant() &&
        this.filterAssistantAutoshow
      ) {
        this.openFilterAssistant();
      }
    }
  }

  onFilter(handler) {
    this._filterEventHandlers.push(handler);
  }

  processFilterValues() {
    for (const value of this.filterValueEntities) {
      const filterCode = value.FILTER_CODE;
      const valueCode = value.FILTER_VALUE_CODE;
      const filterLogic = value.FILTER_LOGIC;
      this.filterObjects[filterCode] = this.filterObjects[filterCode] ?? {
        filterCode: filterCode,
        label: value.FILTER_NAME,
        description: value.FILTER_DESCRIPTION,
        logic: filterLogic,
        filterImgSrc: value.FILTER_MEDIA_URL ? `images_dynam/tp_liste/${value.FILTER_MEDIA_URL}` : '',
      };
      this.filters[filterCode] = this.filters[filterCode] ?? [];
      this.filterLabels[filterCode] = value.FILTER_NAME;
      this.filterLogic[filterCode] = filterLogic;
      const currentFilterValues = this.filters[filterCode];
      if (
        !currentFilterValues.find(val => val.valueCode === valueCode) // only add new filter values
      ) {
        const newItem = {
          valueCode: valueCode,
          label: value.FILTER_VALUE_NAME,
          description: value.FILTER_VALUE_DESCRIPTION,
          order: value.FILTER_VALUE_ORDER,
          isDefault: !!value.FILTER_VALUE_IS_DEFAULT,
          filterLogic: filterLogic,
          filterValueImgSrc: value.FILTER_VALUE_MEDIA_URL
            ? `images_dynam/tp_liste/${value.FILTER_VALUE_MEDIA_URL}`
            : '',
        };
        currentFilterValues.push(newItem);
        if (newItem.isDefault) {
          this.addActiveFilter(filterCode, newItem.valueCode);
        }
      }
    }

    for (const [key] of Object.entries(this.filters)) {
      if (!this.filters[key].length) {
        // remove empty filters
        delete this.filters[key];
      } else {
        // or sort values accordingly
        this.filters[key].sort((a, b) => a.order - b.order);
      }
    }
  }

  initNewFilters() {
    if (!Object.entries(this.filters).length && this.filterSidebar) {
      this.filterSidebar.style.display = 'none';
      this.handleResetInfo();
      return;
    }

    const filterObjects = [];
    for (const [filterCode, filterValues] of Object.entries(this.filters)) {
      filterObjects.push(this.renderFilter(filterCode, filterValues));
    }

    this.filterSidebar.innerHTML = '';

    if (this.filterAssistantEnabled) {
      const filterAssistantObjects = [];
      for (const [filterCode, filterValues] of Object.entries(this.filters)) {
        filterAssistantObjects.push(this.renderAssistantFilter(filterCode, filterValues));
      }
      this.filteringAssistantDialog
        .querySelector('#filtering-assistant-content')
        .append(<div class="filter-assistant-accordion-container">{filterAssistantObjects}</div>);
      this._accordionsAssistant = initAccordions('filter-assistant-accordion-value-container');
      this.filterSidebar.append(
        <button
          class="tp-button tp-button-outline"
          onClick={event => {
            event.preventDefault();
            this.openFilterAssistant();
          }}
        >
          {tp_trans.trans('show_filter_assistant', 'show_filter_assistant', 'show_filter_assistant')}
        </button>
      );
    }

    this.filterSidebar.append(<div class="filter-accordion-container">{filterObjects}</div>);
    this._accordionsSidebar = initAccordions('filter-accordion-value-container');
  }

  openFilterAssistant() {
    closeAccordions(this._accordionsAssistant);
    this.filteringAssistantDialog.showModal();
    document.body.style.overflow = 'hidden';
    openAccordionsBySelectedValues(
      document.querySelectorAll('.filter-assistant-accordion-value-container input:checked'),
      this._accordionsAssistant
    );
  }

  closeFilterAssistant() {
    this.filteringAssistantDialog.close();
    document.body.style.overflow = '';
  }

  renderFilter(filterCode, filterValues) {
    const items = filterValues.map((value, i) => {
      const id = this.getFilterItemId(value.valueCode);
      return (
        <div>
          <label for={id} class="filter-value tp-checkbox">
            <input
              type="checkbox"
              id={id}
              onChange={event => this.filterValueChanged(value.valueCode, filterCode, event.target.checked)}
              class="checkbox-input"
              checked="false"
            />
            <span class="checkbox-label">{value.label}</span>
          </label>
        </div>
      );
    });
    return (
      <div class="filter-accordion-value-container">
        <div class="package-filter ac accordion-opened-default">
          <div class="ac-header">
            <div class="ac-trigger filter-name">
              <strong>{filterCode}</strong>
            </div>
          </div>
          <div class="ac-panel">
            <div class="filter-values">{items}</div>
          </div>
        </div>
      </div>
    );
  }

  renderAssistantFilter(filterCode, filterValues) {
    const items = filterValues.map((value, i) => {
      const id = this.getFilterAssistantItemId(value.valueCode);
      const imageDom = value.filterValueImgSrc ? (
        <div class="product-image">
          <img src={value.filterValueImgSrc} alt="" draggable="false" />
        </div>
      ) : (
        ''
      );
      return (
        <label class="product-wrapper" for={id}>
          <input
            type="checkbox"
            class="plain"
            id={id}
            name={`filter-${filterCode}`}
            onChange={event => this.filterValueChanged(value.valueCode, filterCode, event.target.checked)}
          />
          <div class="product">
            {imageDom}
            <div class="product-name">{value.label}</div>
            <div class="product-desc">{value.description && <p class="product-code">{value.description}</p>}</div>
          </div>
        </label>
      );
    });

    const filterObject = this.filterObjects[filterCode];
    const imageDom = filterObject.filterImgSrc ? (
      <div class="product-group-image">
        <img src={filterObject.filterImgSrc} alt="" />
      </div>
    ) : (
      ''
    );

    return (
      <div class="filter-assistant-accordion-value-container">
        <div class="ac">
          <div class="ac-header">
            <div class="ac-trigger product-group product-group--compact">
              {imageDom}
              <div class="product-group-desc">
                <div class="product-group-name">{filterObject.label}</div>
                <div class="product-group-description">{filterObject.description}</div>
              </div>
            </div>
          </div>
          <div class="ac-panel">
            <div class="products-container per-row-max-count-5-xl">{items}</div>
          </div>
        </div>
      </div>
    );
  }

  getFilterItemId(valueCode) {
    return `filter-value-${generateValidId(valueCode)}`;
  }

  getFilterAssistantItemId(valueCode) {
    return `filter-assistant-value-${generateValidId(valueCode)}`;
  }

  filterValueChanged(valueCode, filterCode, checked) {
    if (checked) {
      this.selectFilterValues(valueCode);
      this.addActiveFilter(filterCode, valueCode);
    } else {
      this.valueDeselected(filterCode, valueCode);
    }
    const event = new UserEvent(
      'package-filter-value',
      'click',
      document.location.pathname,
      `${filterCode}: ${valueCode}`
    );
    EventTracking.submitEvent(event);
    this.filterPackages();
  }

  addActiveFilter(filterCode, valueCode) {
    this.activeFilters[filterCode] = this.activeFilters[filterCode] ?? [];
    this.activeFilters[filterCode].push(valueCode);
    this.activeTags = this.activeTags ?? [];
    this.activeTags.push({ filter: filterCode, value: valueCode });
  }

  valueDeselected(filterCode, valueCode) {
    this.activeFilters[filterCode] = this.activeFilters[filterCode] ?? [];
    this.activeTags = this.activeTags ?? [];
    this.activeFilters[filterCode] = this.activeFilters[filterCode].filter(value => value !== valueCode);
    this.activeTags = this.activeTags.filter(tag => tag.filter !== filterCode || tag.value !== valueCode);
    if (!this.activeFilters[filterCode].length) {
      delete this.activeFilters[filterCode];
    }
  }

  filterPackages() {
    if (this.haveSomeActiveFilters()) {
      this.activePackageIds = this.getPackageIdsForFilters(this.activeFilters);
      document.querySelectorAll('.height-package').forEach(val => (val.style.display = 'none'));
      for (const packageId of this.activePackageIds) {
        document.querySelector(`[data-package-id="${packageId}"]`).style.display = '';
      }
    } else {
      this.activePackageIds = [...this.packageIds];
      document.querySelectorAll('.height-package').forEach(val => (val.style.display = ''));
    }
    this.filterGroups();
    this.renderTags();
    this.handleResetInfo();
    this.handleFilterCounter();
    this.maintainFiltersState();
    runFilterEventHandlers(this, this._filterEventHandlers);
  }

  getActivePackageIds() {
    return this.activePackageIds;
  }

  getPackageIdsForFilters(activeFilters) {
    let activePackageIds = [];
    let isFirstFilter = true;
    for (const [filterCode, valueCodes] of Object.entries(activeFilters)) {
      const matchingEntities = this.filterValueEntities.filter(valueEntity => {
        return valueEntity.FILTER_CODE === filterCode && valueCodes.includes(valueEntity.FILTER_VALUE_CODE);
      });
      let uniquePackageIds = [];
      if (this.filterLogic[filterCode] === FILTER_LOGIC_OR) {
        // Filter logic is OR: we take every item found
        uniquePackageIds = [...new Set(matchingEntities.map(valueEntity => valueEntity.PACKAGE_ID))];
      } else if (this.filterLogic[filterCode] === FILTER_LOGIC_AND) {
        // Filter logic is AND: we take every package id that supports all the valueCodes
        const packageIdsWithValues = matchingEntities.reduce((acc, { PACKAGE_ID, FILTER_CODE }) => {
          const id = PACKAGE_ID;
          if (!acc[id]) {
            acc[id] = [];
          }
          acc[id].push(FILTER_CODE);
          return acc;
        }, {});

        for (const [packageId, matchingValues] of Object.entries(packageIdsWithValues)) {
          if (matchingValues.length === valueCodes.length) {
            uniquePackageIds.push(packageId);
          }
        }
      }

      if (isFirstFilter) {
        activePackageIds = [...uniquePackageIds];
        isFirstFilter = false;
      } else {
        activePackageIds = activePackageIds.filter(packageId => uniquePackageIds.includes(packageId));
      }
    }

    return activePackageIds;
  }

  filterGroups() {
    document.querySelectorAll('.accordion-container .ac').forEach(val => {
      val.style.display = '';
      if (!val.querySelector('.product-wrapper:not([style*="display: none"])')) {
        val.style.display = 'none';
      }
    });
  }

  handleResetInfo() {
    if (this.resetButton) {
      this.resetInfo.style.display = 'none';
    }
  }

  handleFilterCounter() {
    if (this.filterCounterOutput) {
      this.filterCounterOutput.innerHTML = this.activePackageIds.length;
    }
  }

  haveSomeActiveFilters() {
    return !!Object.entries(this.activeFilters).length;
  }

  haveSomeFilters() {
    return !!Object.entries(this.filters).length;
  }

  initResetButton() {
    this.resetButton.addEventListener('click', () => {
      this.resetFilters();
      const event = new UserEvent('package-filter-reset', 'click', document.location.pathname);
      EventTracking.submitEvent(event);
    });
    this.handleResetInfo();
  }

  resetFilters() {
    this.filterSidebar.querySelectorAll('input[type="checkbox"]').forEach(checkbox => (checkbox.checked = false));
    this.activeFilters = {};
    this.activeTags = [];
    this.filterPackages();
  }

  renderTags() {
    const tagOutlets = [this.tagBar, this.tagBarAssistant];
    tagOutlets.forEach(tagBar => {
      if (tagBar) {
        const tags = this.activeTags.map(tag => {
          const filterValueItem = this.filters[tag.filter].find(filterValue => filterValue.valueCode === tag.value);
          return (
            <div class="filter-tag" onClick={() => this.removeTag(tag)}>
              {filterValueItem.label}
              <span></span>
            </div>
          );
        });
        tagBar.innerHTML = '';
        tagBar.append(
          <div>
            {tags}
            {tags.length > 1 ? (
              <div class="filter-tag filter-tag-highlighted" onClick={() => this.resetFilters()}>
                {tp_trans.trans('reset_filters', 'reset_filters')}
                <span></span>
              </div>
            ) : (
              ''
            )}
          </div>
        );
      }
    });
  }

  removeTag(tag) {
    this.valueDeselected(tag.filter, tag.value);
    this.deselectFilterValue(tag.value);
    this.filterPackages();
  }

  deselectFilterValue(value) {
    const id = this.getFilterItemId(value);
    document.getElementById(id).checked = false;
  }

  selectFilterValues(value) {
    const id = this.getFilterItemId(value);
    document.getElementById(id).checked = true;

    if (this.filterAssistantEnabled) {
      const assistantId = this.getFilterAssistantItemId(value);
      document.getElementById(assistantId).checked = true;
    }
  }

  maintainFiltersState() {
    for (const [filterCode, filterValues] of Object.entries(this.filters)) {
      for (const filterValue of filterValues) {
        const id = this.getFilterItemId(filterValue.valueCode);
        const checkbox = document.getElementById(id);
        const disabledState = this.shouldBeFilterValueDisabled(filterCode, filterValue);
        const selectedState = this.isValueSelected(filterValue.valueCode, filterCode);
        checkbox.disabled = disabledState;
        checkbox.checked = selectedState;

        if (this.filterAssistantEnabled) {
          const assistantId = this.getFilterAssistantItemId(filterValue.valueCode);
          const assistantCheckbox = document.getElementById(assistantId);
          assistantCheckbox.disabled = disabledState;
          assistantCheckbox.checked = selectedState;
        }
      }
    }
  }

  shouldBeFilterValueDisabled(filterCode, filterValue) {
    if (!this.activeFilters?.[filterCode]?.includes(filterValue.valueCode)) {
      // simulate if adding the tested filter value to the active filters
      const tempFilters = { ...this.activeFilters };
      const tempFiltersCurrentExclusive = { ...this.activeFilters };
      tempFiltersCurrentExclusive[filterCode] = [filterValue.valueCode];
      tempFilters[filterCode] = tempFilters[filterCode] ? [...tempFilters[filterCode]] : [];
      tempFilters[filterCode].push(filterValue.valueCode);
      const filterLogic = this.filterLogic[filterCode];
      if (filterLogic === FILTER_LOGIC_AND) {
        return !this.getPackageIdsForFilters(tempFilters).length; // using the filter results in having zero packages
      } else if (filterLogic === FILTER_LOGIC_OR) {
        return !this.getPackageIdsForFilters(tempFiltersCurrentExclusive).length; // using the current filter as the only one of the group results in having zero packages
      }
    }

    return false;
  }

  initFilterAssistant() {
    this.filteringAssistantDialog = this.renderFilteringDialog();
    document.body.appendChild(this.filteringAssistantDialog);
    this.tagBarAssistant = document.getElementById(ELEMENT_IDS.packageFilterTagsAssistant);
  }

  renderFilteringDialog() {
    return (
      <dialog id="filtering-assistant-dialog" class="filtering-assistant" style="position:fixed;">
        <div class="filtering-assistant-header">
          <h3 class="filtering-assistant-headline">
            {tp_trans.trans('header_filter_assistant', 'header_filter_assistant', 'header_filter_assistant')}
          </h3>
          <button role="button" class="filtering-assistant-close" onClick={() => this.closeFilterAssistant()}>
            <span class="fa fa-times fa-lg"></span>
          </button>
          <div id="package-filters-tags-assistant"></div>
        </div>
        <div class="filtering-assistant-content">
          <div class="wrapper" id="filtering-assistant-content"></div>
        </div>
        <div class="filtering-assistant-footer">
          <label class="filter-assistant-dismiss tp-checkbox">
            <input
              type="checkbox"
              id="filtering-assistant-checkbox-dismiss"
              class="checkbox-input"
              onChange={event => this.showAssistantHandler(event)}
              checked={this.filterAssistantShowDismissed()}
            />
            <span class="checkbox-label">
              {tp_trans.trans('dismiss_filter_assistant', 'dismiss_filter_assistant', 'dismiss_filter_assistant')}
            </span>
          </label>
          <button class="tp-button tp-button-outline tp-button-inline" onClick={() => this.closeFilterAssistant()}>
            {tp_trans.trans('close_filter_assistant', 'close_filter_assistant', 'close_filter_assistant')}
          </button>
        </div>
      </dialog>
    );
  }

  isValueSelected(valueCode, filterCode) {
    return this.activeFilters[filterCode]?.includes(valueCode);
  }

  storeDoNotShowFilterAssistant() {
    const cookieValue = 'true';
    const daysToExpire = 30;

    const date = new Date();
    date.setTime(date.getTime() + daysToExpire * 24 * 60 * 60 * 1000);
    const expires = 'expires=' + date.toUTCString();

    document.cookie = `${ASSISTANT_COOKIE_NAME}=${cookieValue};${expires};path=/`;
  }

  storeShowFilterAssistant() {
    document.cookie = `${ASSISTANT_COOKIE_NAME}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/`;
  }

  shouldShowFilterAssistant() {
    return !this.filterAssistantShowDismissed();
  }

  filterAssistantShowDismissed() {
    return document.cookie.includes(`${ASSISTANT_COOKIE_NAME}=true`);
  }

  showAssistantHandler(event) {
    if (event.target.checked) {
      this.storeDoNotShowFilterAssistant();
    } else {
      this.storeShowFilterAssistant();
    }
  }
}

function runFilterEventHandlers(packageFiltering, filterEventHandlers) {
  filterEventHandlers.forEach(handler => {
    handler(packageFiltering.activePackageIds);
  });
}
